import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Select, Skeleton, Button, Tooltip } from 'src/common';
import styles from './index.module.scss';
import { useToggle } from 'react-use';
import { useGetOrgs } from 'src/models/organisations/hooks';
import { OnVisibilityTrigger, useFetchMore } from 'src/common/infinityScroll';
import { DEFAULT_PAGE_SIZE } from 'src/models/organisations/types';

interface IProps {
  orgNumbers: string[];
  addOrgs: (selectedIds: string[], updatingIds: string[]) => void;
  addOrgsLoading?: boolean;
}

interface IFormState {
  companiesIds: string[];
}

export const AdminCompaniesForm: FC<IProps> = props => {
  const { orgNumbers, addOrgs, addOrgsLoading } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [searchPhrase, setSearchPhrase] = useState('');
  const [searchByIds, toggleSearchByIds] = useToggle(true);

  const { data: orgs, loading, fetchMore, fetchingMore } = useGetOrgs(searchPhrase);

  const formRef = useRef(null);

  const initialValues = useMemo((): IFormState => {
    return {
      companiesIds: []
    };
  }, []);

  const onFinish = useCallback(
    (values: IFormState) => {
      const { companiesIds } = values;
      const uniqCompaniesIds = [...new Set([...orgNumbers, ...companiesIds])];
      addOrgs(companiesIds, uniqCompaniesIds);
      form.setFieldsValue({ companiesIds: [] });
      setSearchPhrase('');
    },
    [orgNumbers, addOrgs, form]
  );

  const onBlur = useCallback(() => {
    setSearchPhrase('');
  }, []);

  const onSearch = useCallback(value => {
    setSearchPhrase(value);
  }, []);

  const onKeyPress = useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const isMore = useMemo(() => {
    return !searchByIds && orgs.length % DEFAULT_PAGE_SIZE === 0 && orgs.length >= DEFAULT_PAGE_SIZE;
  }, [orgs.length, searchByIds]);

  const loadMore = useCallback(
    (variables: { resultOffset: number }) => {
      return fetchMore({
        variables
      });
    },
    [fetchMore]
  );

  const onLoadMore = useFetchMore({ resultOffset: orgs.length }, loadMore);

  return (
    <div className={styles.companiesForm}>
      <Form
        form={form}
        onFinish={onFinish}
        className={styles.form}
        layout={'vertical'}
        initialValues={initialValues}
        onKeyPress={e => onKeyPress(e)}
        ref={formRef}
      >
        <Form.Item
          name={'companiesIds'}
          label={searchByIds ? t('Tenders.BuyersFilter.orgNumbers') : t('Tenders.BuyersFilter.orgNames')}
        >
          <Select
            mode={searchByIds ? 'tags' : 'multiple'}
            style={{ width: '100%' }}
            tokenSeparators={searchByIds ? [',', ' '] : undefined}
            placeholder={searchByIds ? t('Tenders.BuyersFilter.addOrgNumbers') : t('AdminPage.searchCompetitors')}
            showSearch={!searchByIds}
            filterOption={false}
            loading={loading}
            onSearch={searchByIds ? undefined : onSearch}
            searchValue={searchByIds ? undefined : searchPhrase}
            onBlur={searchByIds ? undefined : onBlur}
            getPopupContainer={trigger => trigger.parentNode}
            notFoundContent={loading ? <Skeleton loading /> : null}
            dropdownRender={menu => <div ref={setContainer}>{menu}</div>}
          >
            {orgs.map(org => (
              <Select.Option value={org.organisationNumber} key={org.organisationNumber} className={styles.option}>
                <Tooltip title={org.organisationName}>
                  <span className={styles.item}>
                    <span className={styles.itemValue}>{org.organisationName}</span>
                    <span className={styles.itemKey}>{org.organisationNumber}</span>
                  </span>
                </Tooltip>
              </Select.Option>
            ))}
            {isMore && (
              <Select.Option value={''}>
                <OnVisibilityTrigger
                  isLoading={fetchingMore}
                  onVisible={onLoadMore}
                  container={container}
                  style={{ display: 'block' }}
                >
                  <Skeleton active loading paragraph />
                </OnVisibilityTrigger>
              </Select.Option>
            )}
          </Select>
        </Form.Item>
        <div className={styles.formFooter}>
          <Button type={'primary'} htmlType={'submit'} loading={addOrgsLoading}>
            {t('AdminPage.addCompetitors')}
          </Button>
        </div>
      </Form>
      <div className={styles.formToggler}>
        <Button type={'link'} onClick={toggleSearchByIds}>
          {searchByIds ? t('Tenders.BuyersFilter.addByOrgNames') : t('Tenders.BuyersFilter.addByOrgNumbers')}
        </Button>
      </div>
    </div>
  );
};

export default AdminCompaniesForm;
